import { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getNotificationAudienceCount } from "app/redux/slice/supplierNotificationSlice";

const NOTIFICATION_TYPES = ["General", "Price Update", "Product", "Offer"];

// Substituted per vendor when the notification is read, so one message can
// carry each vendor's own party rate.
const PLACEHOLDERS = [
  { token: "{{product}}", hint: "Selected product name" },
  { token: "{{price}}", hint: "That vendor's party rate on the product" },
  { token: "{{supplier}}", hint: "Vendor name" },
];

const AUDIENCE_OPTIONS = [
  {
    value: "ALL",
    label: "All vendors",
    help: "Every Approved vendor.",
  },
  {
    value: "PRODUCT_RATE",
    label: "Vendors who have a rate on the selected product",
    help: "Only the vendors that hold a party rate on this product. Each one sees their own rate in {{price}}.",
  },
  {
    value: "SELECTED",
    label: "Specific vendors",
    help: "Only the vendors you pick below.",
  },
];

const SendNotification = ({ onClose, handleSubmit }) => {
  const dispatch = useDispatch();
  const supplierData = useSelector((state) => state?.supplier);
  const productData = useSelector((state) => state?.product);
  const mobileView = useMediaQuery("(max-width:575px)");

  // Live "this will reach N vendors" preview, resolved by the same server-side
  // code the send itself uses.
  const [reach, setReach] = useState({ loading: false, count: null });

  useEffect(() => {
    dispatch(getSupplierList({ sort: 1, field: "name" }));
    dispatch(getProductList({ sort: 1, field: "name" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    title: Yup.string()
      .trim()
      .required("Please enter a title")
      .max(120, "Title must not exceed 120 characters"),
    message: Yup.string()
      .trim()
      .required("Please enter a message")
      .max(1000, "Message must not exceed 1000 characters"),
    type: Yup.string().required("Please select a type"),
    audience: Yup.string().required(),
    product: Yup.mixed().when("audience", {
      is: "PRODUCT_RATE",
      then: (schema) =>
        schema
          .nullable()
          .required("Please select a product - it decides who gets this"),
      otherwise: (schema) => schema.nullable(),
    }),
    assignedSuppliers: Yup.array().when("audience", {
      is: "SELECTED",
      then: (schema) => schema.min(1, "Please pick at least one vendor"),
      otherwise: (schema) => schema,
    }),
  });

  return (
    <Formik
      initialValues={{
        title: "",
        message: "",
        type: "General",
        audience: "ALL",
        product: null,
        assignedSuppliers: [],
      }}
      validationSchema={validationSchema}
      onSubmit={(data, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(data, () => setSubmitting(false));
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => (
        <Form>
          <AudiencePreview
            values={values}
            dispatch={dispatch}
            reach={reach}
            setReach={setReach}
          />
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={8}>
              <JumboAppTextField fullWidth name="title" label="Title*" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                error={touched.type && Boolean(errors.type)}
              >
                <InputLabel>Type*</InputLabel>
                <Select
                  name="type"
                  value={values.type}
                  label="Type*"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {NOTIFICATION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {touched.type && errors.type && (
                  <FormHelperText>{errors.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Product-wise */}
            <Grid item xs={12}>
              <JumboAutoComplete
                options={productData?.list ?? []}
                label={
                  values.audience === "PRODUCT_RATE"
                    ? "Product*"
                    : "Product (optional)"
                }
                getOptionLabel={(option) => option?.name || ""}
                value={values.product}
                name="product"
                error={touched.product && Boolean(errors.product)}
                helperText={(touched.product && errors.product) || ""}
                onChange={(evt, value) => setFieldValue("product", value)}
              />
              <Typography variant="caption" color="text.secondary">
                {values.product
                  ? "Each vendor's own party rate on this product fills in {{price}}."
                  : "No product selected - {{price}} will have nothing to fill in."}
              </Typography>
            </Grid>

            {/* User-wise */}
            <Grid item xs={12}>
              <FormControl>
                <Typography variant="h6" mb={0}>
                  Send To
                </Typography>
                <RadioGroup
                  name="audience"
                  value={values.audience}
                  onChange={(e) => {
                    setFieldValue("audience", e.target.value);
                    // Picked vendors are meaningless on the other two
                    // audiences, and leaving them behind would show a stale
                    // list under a hidden field.
                    if (e.target.value !== "SELECTED") {
                      setFieldValue("assignedSuppliers", []);
                    }
                  }}
                >
                  {AUDIENCE_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={
                        <>
                          <Typography variant="body1" component="span">
                            {option.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {option.help}
                          </Typography>
                        </>
                      }
                      sx={{ alignItems: "flex-start", mb: 1 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            {values.audience === "SELECTED" && (
              <Grid item xs={12}>
                <JumboAutoComplete
                  isMulti
                  options={supplierData?.list ?? []}
                  label="Vendors*"
                  getOptionLabel={(option) => option?.name || ""}
                  value={values.assignedSuppliers}
                  name="assignedSuppliers"
                  error={
                    touched.assignedSuppliers &&
                    Boolean(errors.assignedSuppliers)
                  }
                  helperText={
                    (touched.assignedSuppliers && errors.assignedSuppliers) ||
                    ""
                  }
                  onChange={(evt, value) =>
                    setFieldValue("assignedSuppliers", value)
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <JumboAppTextField
                fullWidth
                multiline
                rows={4}
                name="message"
                label="Message*"
              />
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
              >
                {PLACEHOLDERS.map(({ token, hint }) => (
                  <Chip
                    key={token}
                    size="small"
                    label={token}
                    title={hint}
                    variant="outlined"
                    onClick={() =>
                      setFieldValue("message", `${values.message || ""}${token}`)
                    }
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Click a tag to add it. Each vendor sees their own values.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Alert
                severity={reach.count === 0 ? "warning" : "info"}
                icon={reach.loading ? <CircularProgress size={18} /> : undefined}
              >
                {reach.loading
                  ? "Working out who this reaches..."
                  : reach.count === null
                    ? "This is sent straight to the vendor app inbox and cannot be edited afterwards."
                    : reach.count === 0
                      ? "This currently reaches no vendor - nothing will be sent."
                      : `This will reach ${reach.count} vendor(s). It goes straight to the app inbox and cannot be edited afterwards.`}
              </Alert>
            </Grid>
          </Grid>

          <Stack
            spacing={{ xs: 1, sm: 2 }}
            direction="row"
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              size="large"
              fullWidth={mobileView}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              loading={isSubmitting}
              loadingIndicator={<CircularProgress size={20} />}
              fullWidth={mobileView}
            >
              Send
            </LoadingButton>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

// Kept as its own component so the effect can depend on the Formik values
// without the parent needing to re-render on every keystroke of the message.
const AudiencePreview = ({ values, dispatch, reach, setReach }) => {
  const { audience, product, assignedSuppliers } = values;
  const productId = product?._id || "";
  const supplierIds = (assignedSuppliers || []).map((s) => s._id).join(",");

  useEffect(() => {
    // Nothing to count until the product that defines the audience is chosen.
    if (audience === "PRODUCT_RATE" && !productId) {
      setReach({ loading: false, count: null });
      return;
    }

    let cancelled = false;
    setReach((prev) => ({ ...prev, loading: true }));

    dispatch(
      getNotificationAudienceCount({
        audience,
        ...(productId && { productId }),
        ...(audience === "SELECTED" && { supplierIds }),
      }),
    )
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setReach({
          loading: false,
          count: res?.data?.data?.count ?? null,
        });
      });

    // A quick change of product or audience must not let an older, slower
    // response overwrite the newer count.
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, productId, supplierIds]);

  return null;
};

export default SendNotification;
